import { useEffect, useRef, useState } from "react";
import { ImageUp, Loader2, Save, X } from "lucide-react";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errorMessage } from "@/lib/api";
import { NUM_ERR, validNum } from "@/components/settings/PricingSection";

const COVER_EXT = ["png", "jpg", "jpeg", "svg", "webp"];
const COVER_ERR = "Cover image must be PNG, JPG, SVG or WebP under 5 MB";
const inputCls = (err) =>
  `h-10 w-full rounded-md border bg-slate-900/70 px-3 text-sm text-slate-100 outline-none focus:border-amber-500/70 ${err ? "border-red-500/70" : "border-slate-800"}`;

function NumField({ label, testId, value, onChange, error }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-wider text-slate-500">{label}</Label>
      <input data-testid={testId} inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)} className={`${inputCls(error)} font-mono-num`} />
      {error && <p data-testid={`${testId}-error`} className="text-xs text-red-400">{error}</p>}
    </div>
  );
}

function CoverImage({ company, onChange }) {
  const input = useRef(null);
  const [url, setUrl] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let href;
    if (!company.has_cover) return setUrl(null);
    api.get("/settings/cover", { responseType: "blob" }).then((r) => { href = URL.createObjectURL(r.data); setUrl(href); }).catch(() => setUrl(null));
    return () => href && URL.revokeObjectURL(href);
  }, [company.has_cover, company.cover_file_id]);

  const upload = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!COVER_EXT.includes(ext) || file.size > 5 * 1024 * 1024) return setError(COVER_ERR);
    setError("");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      onChange((await api.post("/settings/cover", fd)).data);
      toast.success("Cover image uploaded");
    } catch (e) {
      setError(errorMessage(e, COVER_ERR));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => onChange((await api.delete("/settings/cover")).data);

  return (
    <div className="space-y-2">
      <Label className="text-[11px] uppercase tracking-wider text-slate-500">Intro cover image (optional)</Label>
      <div className="flex items-center gap-4">
        <div className="grid h-20 w-32 place-items-center overflow-hidden rounded-lg border border-slate-800 bg-slate-900">
          {url ? <img data-testid="cover-preview-image" src={url} alt="Cover" className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-slate-600">None</span>}
        </div>
        <input ref={input} type="file" accept=".png,.jpg,.jpeg,.svg,.webp" className="hidden" data-testid="settings-cover-upload-input"
          onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
        <button data-testid="upload-cover-button" onClick={() => input.current?.click()} disabled={busy}
          className="inline-flex h-9 items-center gap-2 rounded-full border border-amber-500/40 px-4 text-sm text-amber-300 hover:bg-amber-500/10 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageUp className="h-4 w-4" />} {company.has_cover ? "Replace" : "Upload"}
        </button>
        {company.has_cover && (
          <button data-testid="remove-cover-button" onClick={remove} className="inline-flex h-9 items-center gap-1 rounded-full px-3 text-sm text-slate-400 hover:text-red-300">
            <X className="h-4 w-4" /> Remove
          </button>
        )}
      </div>
      {error && <p data-testid="cover-error" className="text-xs text-red-400">{error}</p>}
    </div>
  );
}

export function EstimateSection({ company, onChange }) {
  const [f, setF] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => setF({
    tax_rate: String(company.tax_rate ?? 0), default_shipping: String(company.default_shipping ?? 0),
    rough_opening_allowance: String(company.rough_opening_allowance ?? 0.5), payment_terms: company.payment_terms || "",
    intro_text: company.intro_text || "", intro_layout: company.intro_layout || "full", warranty_text: company.warranty_text || "",
  }), [company.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    const errs = {};
    ["tax_rate", "default_shipping", "rough_opening_allowance"].forEach((k) => { if (!validNum(f[k])) errs[k] = NUM_ERR; });
    if (!errs.tax_rate && Number(f.tax_rate) > 100) errs.tax_rate = "Tax rate must be 100 or less";
    setErrors(errs);
    if (Object.keys(errs).length) return toast.error("Fix the highlighted fields. Nothing was saved.");
    setSaving(true);
    try {
      const { data } = await api.put("/settings/estimate", { ...f, tax_rate: Number(f.tax_rate), default_shipping: Number(f.default_shipping),
        rough_opening_allowance: Number(f.rough_opening_allowance) });
      onChange(data);
      toast.success("Estimate settings saved");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  return (
    <section className="rounded-2xl border border-slate-800 bg-[#F2F2F7]/60 p-6" data-testid="settings-estimate-section">
      <h2 className="text-base font-bold text-slate-100 md:text-lg">Estimate defaults</h2>
      <p className="mt-1 text-sm text-slate-400">Pre-filled on new quotes and printed on your PDF estimates.</p>
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <NumField label="Tax rate (%)" testId="settings-tax-rate-input" value={f.tax_rate ?? ""} onChange={set("tax_rate")} error={errors.tax_rate} />
        <NumField label="Shipping & delivery ($)" testId="settings-shipping-input" value={f.default_shipping ?? ""} onChange={set("default_shipping")} error={errors.default_shipping} />
        <NumField label="Rough opening +in" testId="settings-ro-allowance-input" value={f.rough_opening_allowance ?? ""} onChange={set("rough_opening_allowance")} error={errors.rough_opening_allowance} />
        <div className="space-y-1.5 sm:col-span-3">
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">Payment terms</Label>
          <input data-testid="settings-payment-terms-input" value={f.payment_terms ?? ""} onChange={(e) => set("payment_terms")(e.target.value)} className={inputCls()} placeholder="e.g. 50% deposit, balance on delivery" />
        </div>
        <div className="space-y-1.5 sm:col-span-3">
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">Intro / about page (leave empty to skip)</Label>
          <Textarea data-testid="settings-intro-text-input" rows={4} value={f.intro_text ?? ""} onChange={(e) => set("intro_text")(e.target.value)} className="border-slate-800 bg-slate-900/70 text-slate-100" />
        </div>
        <div className="sm:col-span-3"><CoverImage company={company} onChange={onChange} /></div>
        <div className="flex flex-col gap-1.5 sm:col-span-3" data-testid="settings-intro-layout">
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">Intro page layout</Label>
          <div className="inline-flex w-fit rounded-full border border-slate-800 bg-slate-900/70 p-1">
            {[["full", "Full-page image"], ["image_text", "Image + text"]].map(([v, label]) => (
              <button key={v} type="button" data-testid={`settings-intro-layout-${v}`} onClick={() => set("intro_layout")(v)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${f.intro_layout === v ? "bg-amber-500 text-slate-950" : "text-slate-400 hover:text-slate-100"}`}>
                {label}
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-500">{f.intro_layout === "image_text"
            ? "The image is shown as large as possible with your intro text below it (text moves to the next page if it doesn't fit)."
            : "The intro image fills the whole page, so brochure text stays readable. Intro text, if any, follows on the next page."}</p>
        </div>
        <div className="space-y-1.5 sm:col-span-3">
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">Warranty / terms page (leave empty to skip; "- " for bullets)</Label>
          <Textarea data-testid="settings-warranty-text-input" rows={5} value={f.warranty_text ?? ""} onChange={(e) => set("warranty_text")(e.target.value)} className="border-slate-800 bg-slate-900/70 text-slate-100" />
        </div>
      </div>
      <button data-testid="save-estimate-settings-button" onClick={save} disabled={saving}
        className="mt-6 inline-flex h-10 items-center gap-2 rounded-full bg-amber-500 px-5 text-sm font-semibold text-slate-950 transition-colors hover:bg-amber-400 disabled:opacity-60">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save estimate settings
      </button>
    </section>
  );
}
